import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus, Trash2, Users as UsersIcon, UserPlus, Link2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const EMPTY_STUDENT = {
  grade_id: "", class_id: "", full_name: "", full_name_ar: "", national_id: "",
  dob: "", gender: "", nationality: "SA", phone: "", email: "", address: "",
  enrollment_number: "", enrollment_date: "", blood_type: "", medical_notes: "",
  bio: "", active: true,
};
const EMPTY_GUARD = { name: "", relation: "", phone: "", email: "", is_primary: false, can_pickup: true };

export default function StudentFormDialog({ open, onOpenChange, schoolId, grades, classes, editing, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY_STUDENT);
  const [guardians, setGuardians] = useState([]);
  const [saving, setSaving] = useState(false);

  // guardian add sub-form
  const [guardMode, setGuardMode] = useState("new"); // "new" | "existing"
  const [guardForm, setGuardForm] = useState(EMPTY_GUARD);
  const [parents, setParents] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState("");
  const [existingRelation, setExistingRelation] = useState("");

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...EMPTY_STUDENT, ...editing } : EMPTY_STUDENT);
      setGuardians(editing?.guardians ? [...editing.guardians] : []);
      setGuardForm(EMPTY_GUARD);
      setSelectedParentId("");
      setExistingRelation("");
      setGuardMode("new");
    }
  }, [open, editing]);

  useEffect(() => {
    if (open && schoolId) {
      api.get(`/parents?school_id=${schoolId}`).then((r) => setParents(r.data || [])).catch(() => setParents([]));
    }
  }, [open, schoolId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addNewGuardian = () => {
    if (!guardForm.name.trim()) { toast.error(t("guardian_name_required")); return; }
    setGuardians((g) => [...g, { ...guardForm }]);
    setGuardForm(EMPTY_GUARD);
  };

  const addExistingGuardian = () => {
    const p = parents.find((x) => x.id === selectedParentId);
    if (!p) { toast.error(t("select_parent_account")); return; }
    if (guardians.some((g) => g.email && g.email === p.email)) {
      toast.error("ولي الأمر مضاف بالفعل");
      return;
    }
    setGuardians((g) => [...g, {
      name: p.name || p.email, relation: existingRelation, phone: p.phone || "",
      email: p.email, is_primary: false, can_pickup: true, user_id: p.id,
    }]);
    setSelectedParentId("");
    setExistingRelation("");
  };

  const removeGuardian = (idx) => setGuardians((g) => g.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.full_name_ar?.trim() && !form.full_name?.trim()) {
      toast.error(t("name_required")); return;
    }
    if (!form.grade_id || !form.class_id) {
      toast.error("اختر الصف والفصل"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, school_id: schoolId, guardians };
      if (!payload.full_name) payload.full_name = payload.full_name_ar;
      if (!payload.gender) delete payload.gender;
      ["class_detail", "grade_detail", "parent_account", "student_account", "wallet",
        "attendance", "behavior", "fees_due", "class_homework_count",
        "created_at", "updated_at"].forEach((k) => delete payload[k]);
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      let sid;
      if (editing) {
        await api.put(`/students/${editing.id}`, payload);
        sid = editing.id;
      } else {
        const r = await api.post("/students", payload);
        sid = r.data.id;
      }
      // Create/link parent accounts for guardians with email
      const withEmail = guardians.filter((g) => g.email && g.email.includes("@"));
      let created = 0, linked = 0, emailsSent = 0;
      for (const g of withEmail) {
        try {
          const r = await api.post(`/students/${sid}/guardians/create-account`, {
            email: g.email, name: g.name, phone: g.phone,
          });
          if (r.data.action === "created") created++; else linked++;
          if (r.data.reset_email_sent) emailsSent++;
        } catch { /* non-blocking */ }
      }
      let msg = editing ? t("updated_successfully") : t("student_created");
      if (created > 0) msg += ` · ✉️ تم إنشاء ${created} حساب ولي أمر وإرسال رابط تعيين كلمة المرور`;
      if (linked > 0) msg += ` · تم ربط ${linked} حساب ولي أمر موجود`;
      toast.success(msg, { duration: 8000 });
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => x.msg || "").filter(Boolean).join(" · ")
        : (typeof d === "string" && d) || d?.message;
      toast.error(msg || t("error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="student-form-dialog">
        <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_student")}</DialogTitle></DialogHeader>
        <Tabs defaultValue="basic" className="mt-1">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="basic" data-testid="sf-tab-basic">{t("basic_info")}</TabsTrigger>
            <TabsTrigger value="guardians" data-testid="sf-tab-guardians">
              <UsersIcon className="w-4 h-4 me-1" /> {t("guardians")}
              {guardians.length > 0 && <Badge className="ms-1.5 h-5 px-1.5 text-[10px]">{guardians.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="enrollment">{t("enrollment_info")}</TabsTrigger>
            <TabsTrigger value="medical">{t("medical")}</TabsTrigger>
            <TabsTrigger value="bio">{t("bio")}</TabsTrigger>
          </TabsList>

          {/* BASIC */}
          <TabsContent value="basic" className="mt-4 grid sm:grid-cols-2 gap-3">
            <div><Label>{t("name_ar")} *</Label><Input value={form.full_name_ar || ""} onChange={(e) => set("full_name_ar", e.target.value)} data-testid="sf-name-ar" /></div>
            <div><Label>{t("name_en")}</Label><Input value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)} dir="ltr" data-testid="sf-name-en" /></div>
            <div><Label>{t("select_grade")} *</Label>
              <Select value={form.grade_id} onValueChange={(v) => set("grade_id", v)}>
                <SelectTrigger data-testid="sf-grade"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{grades.map((g) => <SelectItem key={g.id} value={g.id}>{g.name_ar || g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("select_class")} *</Label>
              <Select value={form.class_id} onValueChange={(v) => set("class_id", v)}>
                <SelectTrigger data-testid="sf-class"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{classes.filter((c) => !form.grade_id || c.grade_id === form.grade_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("national_id")}</Label><Input value={form.national_id || ""} onChange={(e) => set("national_id", e.target.value)} dir="ltr" /></div>
            <div><Label>{t("date_of_birth")}</Label><Input type="date" value={form.dob || ""} onChange={(e) => set("dob", e.target.value)} /></div>
            <div>
              <Label>{t("gender")}</Label>
              <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t("gender_male")}</SelectItem>
                  <SelectItem value="female">{t("gender_female")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("nationality")}</Label><Input value={form.nationality || ""} onChange={(e) => set("nationality", e.target.value)} /></div>
            <div><Label>{t("phone")}</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} dir="ltr" /></div>
            <div><Label>{t("email")}</Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} dir="ltr" /></div>
            <div className="sm:col-span-2"><Label>{t("address")}</Label><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
          </TabsContent>

          {/* GUARDIANS */}
          <TabsContent value="guardians" className="mt-4 space-y-4">
            <div className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg p-3">
              💡 {t("guardians_optional_hint")}
            </div>

            {guardians.length > 0 && (
              <div className="space-y-2">
                {guardians.map((g, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-border" data-testid={`sf-guardian-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{g.name}</span>
                        {g.is_primary && <Badge className="bg-amber-500 hover:bg-amber-500 text-xs">{t("primary_guardian")}</Badge>}
                        {g.user_id && <Badge variant="outline" className="text-xs border-emerald-400 text-emerald-700"><Link2 className="w-3 h-3 me-1" />مرتبط بحساب</Badge>}
                      </div>
                      <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        {g.relation && <span>{g.relation}</span>}
                        {g.phone && <span className="font-mono" dir="ltr"><Phone className="w-3 h-3 inline me-1" />{g.phone}</span>}
                        {g.email && <span><Mail className="w-3 h-3 inline me-1" />{g.email}</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeGuardian(idx)} data-testid={`sf-remove-guardian-${idx}`}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="border border-dashed border-border rounded-xl p-4 space-y-3">
              <div className="flex gap-2">
                <Button size="sm" variant={guardMode === "new" ? "default" : "outline"} onClick={() => setGuardMode("new")} data-testid="sf-guard-mode-new">
                  <UserPlus className="w-4 h-4 me-1" /> {t("create_new_guardian")}
                </Button>
                <Button size="sm" variant={guardMode === "existing" ? "default" : "outline"} onClick={() => setGuardMode("existing")} data-testid="sf-guard-mode-existing">
                  <Link2 className="w-4 h-4 me-1" /> {t("link_existing_parent")}
                </Button>
              </div>

              {guardMode === "new" ? (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>{t("name")} *</Label><Input value={guardForm.name} onChange={(e) => setGuardForm({ ...guardForm, name: e.target.value })} data-testid="sf-g-name" /></div>
                    <div><Label>{t("relation")}</Label><Input value={guardForm.relation} onChange={(e) => setGuardForm({ ...guardForm, relation: e.target.value })} placeholder="أب / أم / عم" /></div>
                    <div><Label>{t("phone")}</Label><Input value={guardForm.phone} onChange={(e) => setGuardForm({ ...guardForm, phone: e.target.value })} dir="ltr" /></div>
                    <div><Label>{t("email")}</Label><Input type="email" value={guardForm.email} onChange={(e) => setGuardForm({ ...guardForm, email: e.target.value })} dir="ltr" data-testid="sf-g-email" /></div>
                  </div>
                  <p className="text-[11px] text-muted-foreground">✉️ عند إدخال بريد إلكتروني، سيتم إنشاء حساب دخول لولي الأمر تلقائياً وإرسال رابط تعيين كلمة المرور.</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={guardForm.is_primary} onChange={(e) => setGuardForm({ ...guardForm, is_primary: e.target.checked })} className="w-4 h-4" />
                      {t("primary_guardian")}
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={guardForm.can_pickup} onChange={(e) => setGuardForm({ ...guardForm, can_pickup: e.target.checked })} className="w-4 h-4" />
                      {t("allowed_to_pickup")}
                    </label>
                    <Button size="sm" variant="secondary" onClick={addNewGuardian} data-testid="sf-add-guardian-btn">
                      <Plus className="w-4 h-4 me-1" /> {t("add")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {parents.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">{t("no_parents_found")}</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label>{t("select_parent_account")} *</Label>
                        <Select value={selectedParentId} onValueChange={setSelectedParentId}>
                          <SelectTrigger data-testid="sf-existing-parent-select"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            {parents.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{(p.name || p.email)} — {p.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>{t("relation")}</Label><Input value={existingRelation} onChange={(e) => setExistingRelation(e.target.value)} placeholder="أب / أم / عم" /></div>
                    </div>
                  )}
                  {parents.length > 0 && (
                    <Button size="sm" variant="secondary" onClick={addExistingGuardian} disabled={!selectedParentId} data-testid="sf-link-parent-btn">
                      <Link2 className="w-4 h-4 me-1" /> {t("link_existing_parent")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ENROLLMENT */}
          <TabsContent value="enrollment" className="mt-4 grid sm:grid-cols-2 gap-3">
            <div><Label>{t("enrollment_number")}</Label><Input value={form.enrollment_number || ""} onChange={(e) => set("enrollment_number", e.target.value)} dir="ltr" /></div>
            <div><Label>{t("enrollment_date")}</Label><Input type="date" value={form.enrollment_date || ""} onChange={(e) => set("enrollment_date", e.target.value)} /></div>
            <div>
              <Label>{t("status")}</Label>
              <Select value={form.active === false ? "false" : "true"} onValueChange={(v) => set("active", v === "true")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{t("active")}</SelectItem>
                  <SelectItem value="false">{t("inactive")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          {/* MEDICAL */}
          <TabsContent value="medical" className="mt-4 space-y-3">
            <div>
              <Label>{t("blood_type")}</Label>
              <Select value={form.blood_type || ""} onValueChange={(v) => set("blood_type", v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bt) =>
                    <SelectItem key={bt} value={bt}>{bt}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("medical_notes")}</Label>
              <Textarea value={form.medical_notes || ""} onChange={(e) => set("medical_notes", e.target.value)} rows={5} placeholder={t("medical_notes_placeholder")} />
            </div>
          </TabsContent>

          {/* BIO */}
          <TabsContent value="bio" className="mt-4">
            <Label>{t("bio")}</Label>
            <Textarea value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} rows={6} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="student-save">
            {saving ? t("saving") + "…" : t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
